/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useState } from 'react';

import { GlobalLayoutAccountDropdown } from '@adp-foundation/layout';
import { useLogout } from '@adp-foundation/account-ui-adapter';
import { I18n } from '@adp-arch/i18n';
import { useUserInfo } from '@adp-arch/foundation-sdk';
import {
  IconCozClock,
  IconCozExit,
  IconCozSetting,
  IconCozDocument,
} from '@coze-arch/coze-design/icons';
import { Typography, Tooltip, Dropdown } from '@coze-arch/coze-design';

import { UserInfoMenu } from './user-info-menu';
import { useAccountSettings } from './account-settings';

interface CreditStatus {
  remaining_percent: number;
  limiting_window: 'weekly' | 'monthly';
  reset_at: number;
  status: 'normal' | 'warning' | 'critical' | 'exhausted' | 'unlimited';
}

const CREDIT_STATUS_REFRESH_INTERVAL_MS = 60_000;
const RELEASE_VERSION = process.env.ADP_RELEASE_VERSION?.trim() || 'dev-local';
const BUILD_TIME = process.env.ADP_BUILD_TIME?.trim() || '';
const GIT_SHA = process.env.ADP_GIT_SHA?.trim() || 'nogit';

const displayReleaseVersion = RELEASE_VERSION.startsWith('v')
  ? RELEASE_VERSION
  : `v${RELEASE_VERSION}`;

const BuildVersionMenuItem = () => {
  const buildTimeLabel = BUILD_TIME
    ? new Date(BUILD_TIME).toLocaleString()
    : 'Not available';
  const gitShaLabel = GIT_SHA === 'nogit' ? 'Not available' : GIT_SHA;

  return (
    <Dropdown.Item
      className="!cursor-default hover:!bg-transparent"
      data-testid="layout_avatar_build-version"
    >
      <Tooltip
        position="right"
        content={
          <div className="text-[12px] leading-[18px]">
            <div>Release: {displayReleaseVersion}</div>
            <div>Built: {buildTimeLabel}</div>
            <div>Commit: {gitShaLabel}</div>
          </div>
        }
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center">
            <div className="mr-[8px] flex items-center coz-fg-secondary text-[16px]">
              <IconCozClock />
            </div>
            <div className="coz-fg-primary text-[14px] leading-[20px] font-[400]">
              Version
            </div>
          </div>
          <div className="coz-fg-secondary font-mono text-[12px] leading-[20px]">
            {displayReleaseVersion}
          </div>
        </div>
      </Tooltip>
    </Dropdown.Item>
  );
};

const CreditQuotaMenuItem = () => {
  const [status, setStatus] = useState<CreditStatus | null>(null);

  useEffect(() => {
    let disposed = false;
    const refresh = async () => {
      try {
        const response = await fetch('/api/credits/me', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        const payload = (await response.json()) as {
          code: number;
          data?: CreditStatus;
        };
        if (!disposed && payload.code === 0 && payload.data) {
          setStatus(payload.data);
        }
      } catch (error) {
        console.warn('Failed to load credit quota status', error);
        if (!disposed) {
          setStatus(null);
        }
      }
    };
    void refresh();
    const timer = window.setInterval(
      refresh,
      CREDIT_STATUS_REFRESH_INTERVAL_MS,
    );
    window.addEventListener('focus', refresh);
    return () => {
      disposed = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  if (!status) {
    return null;
  }

  const warning =
    status.status === 'warning' ||
    status.status === 'critical' ||
    status.status === 'exhausted';
  const windowLabel = I18n.t(
    status.limiting_window === 'monthly'
      ? 'credit_quota_monthly'
      : 'credit_quota_weekly',
    {},
    status.limiting_window === 'monthly' ? 'Monthly quota' : 'Weekly quota',
  );
  const tooltip = `${windowLabel} · ${I18n.t(
    'credit_quota_resets_at',
    {},
    'Resets at',
  )} ${new Date(status.reset_at).toLocaleString()}`;

  return (
    <Dropdown.Item
      className="!cursor-default hover:!bg-transparent"
      data-testid="layout_avatar_credit-quota"
    >
      <Tooltip content={tooltip} position="right">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center">
            <div className="mr-[8px] flex items-center coz-fg-secondary text-[16px]">
              <IconCozDocument />
            </div>
            <div className="coz-fg-primary text-[14px] leading-[20px] font-[400]">
              {I18n.t('credit_quota_label', {}, 'Credit')}
            </div>
          </div>
          <div
            className={
              warning
                ? 'text-[#dc2626] font-[600] text-[14px] leading-[20px]'
                : 'coz-fg-hglt font-[600] text-[14px] leading-[20px]'
            }
          >
            {status.remaining_percent}%
          </div>
        </div>
      </Tooltip>
    </Dropdown.Item>
  );
};

export const AccountDropdown = ({
  collapsed = false,
  accountName,
}: {
  collapsed?: boolean;
  accountName?: string;
}) => {
  const [visible, setVisible] = useState(false);
  const userInfo = useUserInfo();
  const { node: logoutModal, open: openLogoutModal } = useLogout();

  const { node: accountSettingsNode, open: openAccountSettings } =
    useAccountSettings();

  if (!userInfo) {
    return null;
  }

  const triggerAccountName =
    accountName?.trim() ||
    userInfo.app_user_info?.user_unique_name ||
    userInfo.name;

  return (
    <GlobalLayoutAccountDropdown
      menus={[
        <UserInfoMenu />,
        <Dropdown.Divider />,
        <CreditQuotaMenuItem />,
        {
          prefixIcon: <IconCozExit />,
          title: I18n.t('settings_api_authorization'),
          onClick: () => {
            openAccountSettings('api-auth');
          },
          dataTestId: 'layout_avatar_api-auth',
        },
        {
          prefixIcon: <IconCozSetting />,
          title: I18n.t('navi_bar_account_settings'),
          onClick: () => {
            openAccountSettings('account');
          },
          dataTestId: 'layout_avatar_profile-settings',
        },
        <BuildVersionMenuItem />,
        <Dropdown.Divider />,
        {
          prefixIcon: <IconCozExit />,
          title: I18n.t('basic_log_out'),
          onClick: () => {
            openLogoutModal();
          },
          dataTestId: 'layout_avatar_logout-button',
        },
      ]}
      visible={visible}
      onVisibleChange={setVisible}
      triggerClassName={
        collapsed
          ? undefined
          : 'w-full min-h-[40px] px-[12px] py-[8px] rounded-[12px] justify-start'
      }
      triggerText={
        collapsed ? null : (
          <Typography.Text
            ellipsis={{ showTooltip: true }}
            className="block text-[14px] leading-[20px] font-[500] coz-fg-primary"
          >
            {triggerAccountName}
          </Typography.Text>
        )
      }
    >
      {logoutModal}
      {accountSettingsNode}
    </GlobalLayoutAccountDropdown>
  );
};
